import { ExponentialBackoff, handleWhen, retry } from 'cockatiel';

export const BYTES_PER_KB = 1024;

// TODO(jesse)[ENG-1563]: Make chunk size configurable
export const LIVE_CHUNK_SIZE_KB = 4;

/**
 * Target duration for each live transcription chunk in seconds.
 * Kept short for responsive interim transcripts.
 */
const LIVE_CHUNK_TARGET_DURATION_SECONDS = 0.5;

/**
 * Compute the live transcription chunk size in bytes based on the Opus bitrate
 * so that each chunk represents a consistent ~0.5s of audio regardless of
 * bitrate. This keeps first-word latency stable when A/B testing bitrates.
 *
 * Falls back to the legacy 4 KB constant when no bitrate is provided.
 */
export const getLiveChunkSizeBytes = (opusBitrate?: number): number => {
  if (!opusBitrate) return LIVE_CHUNK_SIZE_KB * BYTES_PER_KB;

  // bitrate is in bits/sec; convert to bytes for the target duration
  const bytes = Math.ceil(
    (opusBitrate * LIVE_CHUNK_TARGET_DURATION_SECONDS) / 8,
  );

  // Clamp to a reasonable range: at least 256 bytes, at most 16 KB
  return Math.max(256, Math.min(bytes, 16 * BYTES_PER_KB));
};

export const PERSISTED_CHUNK_SIZE_KB = 16;

const networkRetryPolicy = retry(
  handleWhen((error) => error instanceof TypeError),
  {
    maxAttempts: 4,
    backoff: new ExponentialBackoff({ initialDelay: 1000 }),
  },
);

/**
 * Wrapper around fetch that retries with exponential backoff on network errors.
 * Only retries when fetch itself throws (e.g. DNS failure, connection refused),
 * not on HTTP error responses.
 */
export const fetchWithNetworkRetry = (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  return networkRetryPolicy.execute(() => fetch(input, init));
};
